/**
 * Size Guide Modal - AJAX Implementation
 * Fetches Shopify Page content and displays in modal
 */

(function() {
  'use strict';

  // Modal state
  let modal = null;
  let overlay = null;
  let isOpen = false;

  /**
   * Initialize the modal system
   */
  function init() {
    // Create modal structure
    createModalStructure();
    
    // Attach event listeners
    attachEventListeners();
  }

  /**
   * Create the modal HTML structure
   */
  function createModalStructure() {
    // Create overlay
    overlay = document.createElement('div');
    overlay.className = 'size-guide-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    
    // Create modal container
    modal = document.createElement('div');
    modal.className = 'size-guide-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'size-guide-title');
    
    // Create modal content structure
    modal.innerHTML = `
      <button class="size-guide-close" aria-label="Close modal" type="button">×</button>
      <div class="size-guide-content">
        <div class="size-guide-loading" style="display: none;">
          <p>Loading...</p>
        </div>
        <div class="size-guide-body"></div>
      </div>
    `;
    
    // Append to body
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    
    // Attach close button listener
    const closeBtn = modal.querySelector('.size-guide-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }
    
    // Close on overlay click
    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) {
        closeModal();
      }
    });
  }

  /**
   * Convert relative URL to absolute URL
   */
  function getAbsoluteUrl(url) {
    if (!url) {
      console.error('getAbsoluteUrl called with empty URL');
      return null;
    }
    
    // If already absolute, return as is (but ensure it's the same origin)
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // If it's a different origin, we might have issues, but return it anyway
      return url;
    }
    
    // Convert relative URL to absolute
    const baseUrl = window.location.origin;
    // Ensure URL starts with /
    const path = url.startsWith('/') ? url : '/' + url;
    const absoluteUrl = baseUrl + path;
    
    console.log('URL conversion:', { input: url, output: absoluteUrl });
    return absoluteUrl;
  }

  /**
   * Attach event listeners to size guide links
   */
  function attachEventListeners() {
    // Use event delegation for dynamic content
    document.addEventListener('click', function(e) {
      const link = e.target.closest('a');
      
      if (!link) return;
      
      // Check if link matches our criteria
      const href = link.getAttribute('href');
      if (!href || href === '#' || href === '') return;
      
      const hasTriggerClass = link.classList.contains('size-guide-trigger');
      const isInProductDescription = link.closest('.product-description') !== null;
      const isPageLink = href.includes('/pages/');
      
      if ((hasTriggerClass || (isInProductDescription && isPageLink))) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Get the exact href value (remove any hash or query params that might interfere)
        let cleanHref = href.split('#')[0].split('?')[0];
        
        // Ensure it's a valid /pages/ URL
        if (!cleanHref.includes('/pages/')) {
          console.warn('Link does not contain /pages/:', cleanHref);
          return;
        }
        
        // Convert to absolute URL
        const absoluteUrl = getAbsoluteUrl(cleanHref);
        
        // Debug: log the link clicked
        console.log('Size guide link clicked:', {
          originalHref: href,
          cleanHref: cleanHref,
          absoluteUrl: absoluteUrl,
          hasTriggerClass: hasTriggerClass,
          isInProductDescription: isInProductDescription,
          linkElement: link
        });
        
        // Verify the URL one more time before opening
        if (absoluteUrl && absoluteUrl.includes('/pages/')) {
          openModal(absoluteUrl);
        } else {
          console.error('Invalid URL generated:', absoluteUrl);
        }
      }
    });
    
    // Close on Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    });
  }

  /**
   * Fetch page content via AJAX
   */
  async function fetchPageContent(url) {
    try {
      // Ensure URL is absolute
      const absoluteUrl = getAbsoluteUrl(url);
      
      // Debug: log the URL being fetched
      console.log('Fetching page content from:', absoluteUrl);
      console.log('Original URL passed:', url);
      
      // Add cache-busting parameter to ensure fresh content
      const cacheBuster = '?_t=' + Date.now();
      const fetchUrl = absoluteUrl + (absoluteUrl.includes('?') ? '&' : '?') + '_t=' + Date.now();
      
      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
          'Cache-Control': 'no-cache'
        },
        credentials: 'same-origin',
        cache: 'no-store',
        redirect: 'follow'
      });
      
      // Check final URL after redirects
      const finalUrl = response.url;
      console.log('Final URL after redirects:', finalUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Debug: Check page title and URL to verify we got the right page
      const pageTitle = doc.querySelector('title') || doc.querySelector('.page-title') || doc.querySelector('h1');
      const pageTitleText = pageTitle ? pageTitle.textContent.trim() : 'No title found';
      console.log('Page title found:', pageTitleText);
      
      // Check if the URL in the document matches what we requested
      const canonicalLink = doc.querySelector('link[rel="canonical"]');
      const canonicalUrl = canonicalLink ? canonicalLink.getAttribute('href') : null;
      console.log('Canonical URL:', canonicalUrl);
      console.log('Requested URL:', absoluteUrl);
      
      // Verify we're on the right page (check if URL contains the expected page handle)
      const expectedPageHandle = absoluteUrl.split('/pages/')[1]?.split('?')[0]?.split('#')[0];
      if (expectedPageHandle && canonicalUrl && !canonicalUrl.includes(expectedPageHandle)) {
        console.warn('WARNING: Page URL mismatch! Expected page handle:', expectedPageHandle, 'but got:', canonicalUrl);
      }
      
      // IMPORTANT: Look for content WITHIN the .main-content area to avoid getting content from other sections
      // The page content is wrapped in .main-content > .shopify-section > .page-content > .page-body
      // NOTE: Some pages might use .origin-story-section instead of .page-body
      const mainContent = doc.querySelector('.main-content');
      
      // Check if this is an origin page (has origin-story-section)
      const originSection = doc.querySelector('.origin-story-section');
      const isOriginPage = originSection !== null;
      
      // Get all .page-body elements for analysis
      const allPageBodies = doc.querySelectorAll('.page-body');
      console.log('Found', allPageBodies.length, '.page-body elements');
      
      // Helper function to check if content matches expected page
      function contentMatchesExpected(element, expectedHandle) {
        if (!expectedHandle) return true;
        
        const elementText = element.textContent.trim().toUpperCase();
        const elementClass = element.className || '';
        
        // If looking for size guide, reject if contains origin keywords or is origin-story-section
        if (expectedHandle.includes('size')) {
          const originKeywords = ['ORIGINS', 'IT ALL STARTED', '727', 'ANGEL NUMBER'];
          const hasOriginContent = originKeywords.some(keyword => elementText.includes(keyword));
          const isOriginSection = elementClass.includes('origin-story');
          
          if (hasOriginContent || isOriginSection) {
            console.log('  ✗ Rejected: Contains origin keywords or is origin section');
            return false;
          }
          // Accept if it has size-related content
          const sizeKeywords = ['SIZE', 'MEASUREMENT', 'INCH', 'CM', 'CHEST', 'WAIST', 'SLEEVE'];
          const hasSizeContent = sizeKeywords.some(keyword => elementText.includes(keyword));
          if (hasSizeContent) {
            console.log('  ✓ Accepted: Contains size-related content');
            return true;
          }
        }
        
        // If looking for origin, accept if contains origin keywords or is origin-story-section
        if (expectedHandle.includes('origin')) {
          const originKeywords = ['ORIGINS', 'IT ALL STARTED', '727'];
          const hasOriginContent = originKeywords.some(keyword => elementText.includes(keyword));
          const isOriginSection = elementClass.includes('origin-story');
          
          if (hasOriginContent || isOriginSection) {
            console.log('  ✓ Accepted: Contains origin keywords or is origin section');
            return true;
          }
          console.log('  ✗ Rejected: Missing origin keywords');
          return false;
        }
        
        return true;
      }
      
      let content = null;
      let candidates = [];
      
      // If this is an origin page and we're looking for origin, use origin-story-section
      if (isOriginPage && expectedPageHandle && expectedPageHandle.includes('origin')) {
        console.log('Origin page detected, using .origin-story-section');
        content = originSection;
      } else {
        // First, collect all candidate .page-body elements within .main-content
        // Skip origin-story-section if we're NOT looking for origin
        if (mainContent) {
          const shopifySections = mainContent.querySelectorAll('.shopify-section');
          console.log('Found', shopifySections.length, '.shopify-section elements in .main-content');
          
          // Collect all .page-body elements from shopify-sections
          // Skip sections that contain origin-story
          for (let section of shopifySections) {
            // Skip origin-story sections if we're not looking for origin
            if (!expectedPageHandle?.includes('origin') && section.querySelector('.origin-story-section')) {
              console.log('Skipping origin-story section');
              continue;
            }
            
            const pageContent = section.querySelector('.page-content');
            const pageBody = section.querySelector('.page-body');
            
            if (pageBody) {
              candidates.push({
                element: pageBody,
                source: 'shopify-section .page-body',
                inMainContent: true
              });
            } else if (pageContent) {
              const body = pageContent.querySelector('.page-body');
              if (body) {
                candidates.push({
                  element: body,
                  source: 'shopify-section .page-content .page-body',
                  inMainContent: true
                });
              }
            }
          }
          
          // Also check direct .page-body in main-content (but not if it's within origin-story)
          const directPageBody = mainContent.querySelector('.page-body');
          if (directPageBody && 
              !candidates.find(c => c.element === directPageBody) &&
              !directPageBody.closest('.origin-story-section')) {
            candidates.push({
              element: directPageBody,
              source: '.main-content .page-body (direct)',
              inMainContent: true
            });
          }
        }
      }
      
      // If no candidates in main-content, check all .page-body elements
      if (candidates.length === 0) {
        console.log('No candidates in .main-content, checking all .page-body elements');
        allPageBodies.forEach((body, index) => {
          const isInMainContent = body.closest('.main-content') !== null;
          candidates.push({
            element: body,
            source: `.page-body[${index}]`,
            inMainContent: isInMainContent
          });
        });
      }
      
      // Filter and rank candidates
      console.log('Evaluating', candidates.length, 'candidates');
      const validCandidates = candidates.filter(candidate => {
        const text = candidate.element.textContent.trim();
        if (text.length < 50) {
          console.log('  ✗ Rejected:', candidate.source, '- Too short');
          return false;
        }
        return true;
      });
      
      // Try to find the best match based on expected page handle
      if (expectedPageHandle && validCandidates.length > 1) {
        console.log('Multiple candidates found, selecting best match for:', expectedPageHandle);
        
        for (let candidate of validCandidates) {
          if (contentMatchesExpected(candidate.element, expectedPageHandle)) {
            // Prefer candidates in main-content
            if (candidate.inMainContent) {
              content = candidate.element;
              console.log('✓ Selected:', candidate.source);
              break;
            }
          }
        }
        
        // If no perfect match, use first valid candidate in main-content
        if (!content) {
          const mainContentCandidates = validCandidates.filter(c => c.inMainContent);
          if (mainContentCandidates.length > 0) {
            content = mainContentCandidates[0].element;
            console.log('✓ Selected first candidate in .main-content:', mainContentCandidates[0].source);
          }
        }
      } else if (validCandidates.length > 0) {
        // Single candidate or no expected handle - use first valid one
        const preferred = validCandidates.find(c => c.inMainContent) || validCandidates[0];
        content = preferred.element;
        console.log('✓ Selected:', preferred.source);
      }
      
      // Last resort: get main content area
      if (!content) {
        content = doc.querySelector('main .main-content') || doc.querySelector('main');
      }
      
      // Final fallback: get body content but exclude header/footer
      if (!content) {
        const body = doc.body;
        content = document.createElement('div');
        content.innerHTML = body.innerHTML;
        
        // Remove header, footer, nav, sidebar, and other sections
        const toRemove = content.querySelectorAll('header, footer, nav, .sidebar-navigation, .mobile-sticky-header, script, style');
        toRemove.forEach(el => el.remove());
      }
      
      if (!content) {
        console.error('No content found in page');
        return '<p>Content not found.</p>';
      }
      
      // Final verification: Ensure content matches expected page
      if (content && expectedPageHandle) {
        const contentText = content.textContent.trim().toUpperCase();
        const isSizeGuide = expectedPageHandle.includes('size');
        const hasOriginContent = contentText.includes('ORIGINS') || contentText.includes('IT ALL STARTED');
        
        if (isSizeGuide && hasOriginContent) {
          console.error('⚠️ WARNING: Size guide page but content contains ORIGINS!');
          console.error('Attempting to find correct content...');
          
          // Try one more time to find the right content
          const allBodies = doc.querySelectorAll('.page-body');
          for (let body of allBodies) {
            const bodyText = body.textContent.trim().toUpperCase();
            if (bodyText.length > 50 && 
                !bodyText.includes('ORIGINS') && 
                !bodyText.includes('IT ALL STARTED') &&
                body.closest('.main-content')) {
              content = body;
              console.log('✓ Found correct content on retry');
              break;
            }
          }
        }
      }
      
      // Debug: Log what we found
      const contentPreview = content.textContent.substring(0, 150).trim();
      console.log('✓ Content extracted successfully');
      console.log('  Selector:', content.className || 'unknown');
      console.log('  Preview:', contentPreview + '...');
      console.log('  Length:', content.textContent.length, 'characters');
      console.log('  Expected page:', expectedPageHandle || 'any');
      
      return content.innerHTML;
    } catch (error) {
      console.error('Error fetching page content:', error);
      console.error('URL attempted:', url);
      return '<p>Error loading content. Please try again later.</p>';
    }
  }

  /**
   * Open the modal and load content
   */
  async function openModal(url) {
    if (!modal || !overlay) {
      createModalStructure();
    }
    
    isOpen = true;
    const loadingEl = modal.querySelector('.size-guide-loading');
    const bodyEl = modal.querySelector('.size-guide-body');
    
    // Show loading state
    loadingEl.style.display = 'block';
    bodyEl.style.display = 'none';
    bodyEl.innerHTML = '';
    
    // Show modal
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('size-guide-overlay--open');
    modal.classList.add('size-guide-modal--open');
    
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    
      // Fetch and display content
    try {
      const content = await fetchPageContent(url);
      bodyEl.innerHTML = content;
      
      // Wrap tables in scrollable container for mobile responsiveness
      const tables = bodyEl.querySelectorAll('table');
      tables.forEach(table => {
        // Check if table is already wrapped
        if (table.parentElement && table.parentElement.classList.contains('table-wrapper')) {
          return;
        }
        
        const wrapper = document.createElement('div');
        wrapper.className = 'table-wrapper';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
      
      // Hide loading, show content
      loadingEl.style.display = 'none';
      bodyEl.style.display = 'block';
      
      // Focus management
      modal.focus();
    } catch (error) {
      loadingEl.style.display = 'none';
      bodyEl.innerHTML = '<p>Error loading content. Please try again later.</p>';
      bodyEl.style.display = 'block';
    }
  }

  /**
   * Close the modal
   */
  function closeModal() {
    if (!isOpen) return;
    
    isOpen = false;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('size-guide-overlay--open');
    modal.classList.remove('size-guide-modal--open');
    
    // Unlock body scroll
    document.body.style.overflow = '';
    
    // Clear content after animation
    setTimeout(() => {
      const bodyEl = modal.querySelector('.size-guide-body');
      if (bodyEl) {
        bodyEl.innerHTML = '';
      }
    }, 300);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
